import { useMemo, useState } from 'react';
import type { Drn } from '@duke-hq/drn';
import type { ChatUser } from '@duke-hq/svc-support-web';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { SupportContextNoop } from './ctx';

export function useChatActions({
  lookupUser,
  studentDrn,
  onClose,
}: {
  lookupUser?: (drn: Drn) => ChatUser | undefined;
  studentDrn?: Drn;
  onClose?: () => void;
}) {
  const [chatUserCache, setChatUserCache] = useState<Record<Drn, ChatUser>>({});

  const sendTyping = trpcTenant.support.sendTyping.useMutation();
  const sendMessage = trpcTenant.support.sendMessage.useMutation();
  const closeChat = trpcTenant.support.chat.close.useMutation();
  const ensureChat = trpcTenant.support.ensureChatForContextDrn.useMutation();

  const utils = trpcTenant.useUtils();

  const chatActions: typeof SupportContextNoop = useMemo(
    () => ({
      archive: async (id) => {
        closeChat.mutate({ id });
        utils.support.chat.listMine.refetch();
        if (onClose) {
          onClose();
        }
      },
      actions: {
        sendTyping: async ({ chatId, typing }) => {
          sendTyping.mutate({ chatId, typing });
        },
        sendMessage: async ({ chatId, message }) => {
          sendMessage.mutate({ chatId, message });
        },
        getUser: (userDrn) => {
          if (chatUserCache && chatUserCache[userDrn]) {
            return chatUserCache[userDrn] as ChatUser | undefined;
          }
          // otherwise look in the original page cache
          if (typeof lookupUser === 'function') {
            const user = lookupUser(userDrn);
            if (user) {
              return user;
            }
          }
        },
        refetchChatMeta: async (chatId: string) => {
          utils.support.chat.getMeta.refetch({ id: chatId });
          utils.support.chat.listMine.refetch();
        },
        createStudentContextChat: async () => {
          ensureChat.mutate({ contextDrn: studentDrn });
        },
      },
    }),
    [
      closeChat,
      utils.support.chat.listMine,
      utils.support.chat.getMeta,
      onClose,
      sendTyping,
      sendMessage,
      chatUserCache,
      lookupUser,
      ensureChat,
      studentDrn,
    ],
  );

  return {
    chatUserCache,
    setChatUserCache,
    chatActions,
  };
}
